import type { MeetingModeWire } from '@ai-meeting/shared'
import type { SessionKind } from '@/constants/session-kind'

/**
 * The kind the form picked, as the socket names it.
 *
 * Spelled out rather than passed through even though the two vocabularies match
 * today: they are separate contracts — one is ours, one is the AI service's —
 * and this is where a rename on either side has to fail to compile instead of
 * silently sending a mode the server does not know.
 */
const WIRE_BY_KIND: Record<SessionKind, MeetingModeWire> = {
  meeting: 'meeting',
  interview: 'interview',
  presentation: 'presentation',
  internal_meeting: 'internal_meeting',
}

/**
 * What to put in `config.meetingMode`, or null to leave the field out.
 *
 * `meeting` is left out on purpose. It is the server's default, so omitting it
 * makes the config byte-identical to the one this app sent before the field
 * existed — which is what keeps the ordinary meeting immune to anything in this
 * feature, including a server that has not been updated yet.
 */
export function meetingModeForSessionKind(kind: SessionKind): MeetingModeWire | null {
  const mode = WIRE_BY_KIND[kind]
  return mode === 'meeting' ? null : mode
}

export type ReadyModeVerdict = 'ok' | 'mismatch' | 'not_echoed'

/**
 * Compare the mode the server echoed on `ready` against the one just sent.
 *
 * The check exists because the config schema ignores fields it does not know:
 * send `internal_meeting` to a server that predates the field and it will
 * happily run an ordinary meeting instead — putting a transcript of a private
 * conversation on screen and through an LLM, with nothing anywhere saying so.
 * The echo is the only signal that this happened.
 *
 * @param sent What went out in `config`, or null when the field was omitted.
 * @param echoed `ready.meetingMode`, which an older server does not send at all.
 */
export function verifyReadyMeetingMode(
  sent: MeetingModeWire | null,
  echoed: unknown
): ReadyModeVerdict {
  if (sent === null) return 'ok'
  if (typeof echoed !== 'string') return 'not_echoed'
  return echoed === sent ? 'ok' : 'mismatch'
}

/**
 * Whether a verdict should stop the meeting rather than just be noted.
 *
 * A disagreement always stops it: the two sides name different modes, and only
 * one of them is running.
 *
 * Silence is judged by what a downgrade would cost. For `internal_meeting` it
 * costs the whole point of the mode, so silence is treated as the failure it
 * probably is. For `interview` and `presentation` the server's own note says
 * they behave exactly like `meeting` and differ only in its logs — so an older
 * server ignoring the field loses a log label and nothing else, and refusing to
 * record the meeting over that would be the larger harm.
 *
 * This is a deliberate narrowing of "fail-closed on any difference": flip
 * `not_echoed` to always fail here if the protocol note is read more strictly.
 */
export function isFatalReadyModeVerdict(
  verdict: ReadyModeVerdict,
  sent: MeetingModeWire | null
): boolean {
  if (verdict === 'mismatch') return true
  return verdict === 'not_echoed' && sent === 'internal_meeting'
}
